import React, { useState, useRef, useCallback, useEffect, useLayoutEffect, useMemo } from "react";
import {
  Card, CardBody, Row, Col, Button, Collapse
} from "reactstrap";
import {
  InstantSearch,
  Hits,
  useHits,
  Configure
} from "react-instantsearch";
import { createElasticsearchAdapter } from "../../utils/elasticsearchAdapter";
import {
  CustomSearchBox,
  CustomStats,
  CustomSortBy,
  CustomRefinementList,
  CustomClearRefinements,
  CustomPagination
} from "../applicant/CustomSearchComponents";
import { useRefinementList } from "react-instantsearch";
import CoursesHits from "./Hits";
import _ from "lodash";

const STATE_LABELS = {
  opened: "Abierto",
  closed: "Cerrado",
  draft: "Borrador"
};

const COMPANY_LABELS = {
  bice_bank: "Banco BICE",
  bk: "BK Servicios",
  bice_investments: "BICE Inversiones",
  bice_corp: "BICE Corp"
};

const CompanyFilterItem = ({ label, bemBlocks, onClick, active, count }) => (
  <div className={bemBlocks.option().state({ active })} onClick={onClick} style={{ cursor: "pointer" }}>
    <input type="checkbox" readOnly checked={active} tabIndex="-1" className={bemBlocks.option("checkbox")} style={{ marginRight: 8 }} />
    <div className={bemBlocks.option("text")}>
      {COMPANY_LABELS[label] || label}
    </div>
    <div className={bemBlocks.option("count")}>{count}</div>
  </div>
);

const StateFilterItem = ({ label, bemBlocks, onClick, active, count }) => (
  <div
    className={bemBlocks.option().state({ active })}
    onClick={onClick}
    style={{ cursor: "pointer" }}
  >
    <input
      type="checkbox"
      readOnly
      checked={active}
      tabIndex="-1"
      className={bemBlocks.option("checkbox")}
      style={{ marginRight: 8 }}
    />
    <div className={bemBlocks.option("text")}>
      {STATE_LABELS[label] || label}
    </div>
    <div className={bemBlocks.option("count")}>{count}</div>
  </div>
);

const filterConfigs = [
  {
    id: "recruiter",
    label: "Reclutador",
    field: "minisite_publication.recruiter.raw",
    placeholder: "Buscar reclutador…"
  },
  {
    id: "aasm_state",
    label: "Estado",
    field: "aasm_state.raw",
    placeholder: "Buscar estado…"
  },
  {
    id: "requesting_company",
    label: "Empresa",
    field: "minisite_publication.requesting_company",
    placeholder: "Buscar empresa…",
    itemComponent: "company"
  }
];

// Fields to search in when user types in search box
const searchFields = [
  "title",
  "created_by.full_name",
  "created_by.email"
];

// Component wrapper to expose refinement state to parent
// This allows us to read refinements directly from InstantSearch state
const RefinementListWithTracking = ({ attribute, searchable, onRefinementChange }) => {
  const { items } = useRefinementList({
    attribute,
    searchable,
    limit: 100
  });

  // Track which items are selected - read directly from InstantSearch state
  const selectedValues = useMemo(() => {
    return items.filter(item => item.isRefined).map(item => item.value);
  }, [items]);

  // Update ref immediately during render (synchronously)
  // This ensures the ref is updated before InstantSearch triggers a new search
  const prevValuesRef = useRef(null);
  const valuesKey = selectedValues.join(',');

  // Update ref synchronously during render if values changed
  // This happens before InstantSearch reads the state for the next search
  if (prevValuesRef.current !== valuesKey) {
    prevValuesRef.current = valuesKey;
    // Call callback immediately (synchronously during render)
    // This updates the parent's ref before InstantSearch triggers search
    if (onRefinementChange) {
      onRefinementChange(attribute, selectedValues);
    }
  }

  // Also ensure update in useLayoutEffect as backup (runs synchronously after DOM updates)
  useLayoutEffect(() => {
    if (onRefinementChange) {
      onRefinementChange(attribute, selectedValues);
    }
  }, [attribute, valuesKey, selectedValues, onRefinementChange]);

  // Just use CustomRefinementList - it will handle the UI
  return (
    <CustomRefinementList
      attribute={attribute}
      searchable={searchable}
    />
  );
};

const CoursesIndex = (props) => {
  const { route, currentUserRole, currentUserEmail, onFocusCourse } = props;

  const [customHits, setCustomHits] = useState([]);
  const [filtrosOpen, setFiltrosOpen] = useState(false);
  const [openFilter, setOpenFilter] = useState(null);
  const [firstFiltersOpen, setFirstFiltersOpen] = useState(false);
  const [sortOption, setSortOption] = useState({ field: "_score", order: "desc" });

  // Use refs to store values so we don't need to recreate the client
  const sortOptionRef = useRef(sortOption);
  const searchFieldsRef = useRef(searchFields);

  // Update refs when values change
  useEffect(() => {
    sortOptionRef.current = sortOption;
  }, [sortOption]);

  useEffect(() => {
    searchFieldsRef.current = searchFields;
  }, []);

  // Memoize Configure props to prevent unnecessary re-renders
  // Note: We don't use sortBy here because we handle sorting in the adapter via customSort
  const configureProps = useMemo(() => ({
    hitsPerPage: 10000
  }), []);

  // Store active refinements to pass to adapter
  // Use a ref that gets updated synchronously during render
  const activeRefinementsRef = useRef({});
  const [, forceUpdate] = useState({});

  // Function to update refinements - called synchronously during render
  const updateRefinements = useCallback((attribute, values) => {
    const updated = { ...activeRefinementsRef.current };
    if (values && values.length > 0) {
      updated[attribute] = values;
    } else {
      delete updated[attribute];
    }
    activeRefinementsRef.current = updated;
  }, []);

  // Create search client with Elasticsearch adapter
  // Only recreate when route changes to avoid resetting pagination
  const searchClient = useMemo(() => {
    // Ensure route ends with /_search
    const searchRoute = route.endsWith('/_search') ? route : `${route}/_search`;

    const adapter = createElasticsearchAdapter(searchRoute, {
      httpHeaders: { "X-CSRF-TOKEN": window.Hupe8.token },
      timeout: 20000
    });

    // Wrap the search method to inject customSort, queryFields, facets, and refinements dynamically
    const originalSearch = adapter.search.bind(adapter);
    adapter.search = async (requests) => {
      // Get all filter attributes that need aggregations
      const filterAttributes = filterConfigs.map(config => config.field);

      const modifiedRequests = requests.map(req => ({
        ...req,
        params: {
          ...req.params,
          customSort: sortOptionRef.current,
          queryFields: searchFieldsRef.current,
          // Ensure facets are always requested for filter attributes
          facets: [...(req.params.facets || []), ...filterAttributes].filter((v, i, a) => a.indexOf(v) === i)
          // Don't inject refinementList from ref - InstantSearch passes it via facetFilters
          // Only use refinementList if InstantSearch passes it directly (fallback)
        }
      }));
      return originalSearch(modifiedRequests);
    };

    return adapter;
  }, [route]);

  const addCourseToHits = useCallback((newHit) => {
    setCustomHits(prev => [newHit, ...prev]);
  }, []);


  const toggleFiltros = useCallback(() => {
    setFiltrosOpen(prev => {
      const open = !prev;
      if (open && !firstFiltersOpen) {
        setFirstFiltersOpen(true);
      }
      return open;
    });
  }, [firstFiltersOpen]);

  const toggleFilterPanel = useCallback((id) => {
    setOpenFilter(prev => prev === id ? null : id);
  }, []);

  const goToList = useCallback((postId) => {
    const tab = [...document.querySelectorAll(".nav-link")]
      .find(n => /postulaciones/i.test(n.textContent || ""));
    tab?.click?.();

    let tries = 0;
    const seek = () => {
      const el = document.getElementById(`list-card-${postId}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.remove("flash-highlight");
        void el.offsetWidth;
        el.classList.add("flash-highlight");
        setTimeout(() => el.classList.remove("flash-highlight"), 3200);
      } else if (++tries < 40) {
        setTimeout(seek, 80);
      }
    };
    setTimeout(seek, 80);
  }, []);

  // Component that renders CoursesHits with converted hits
  // This avoids the state update loop by using useHits directly
  const CoursesHitsWrapper = React.memo(({
    customHits,
    addCourseToHits,
    currentUserRole,
    currentUserEmail,
    onFocusCourse
  }) => {
    const { hits: instantHits } = useHits();

    // Convert hits using useMemo to prevent unnecessary recalculations
    const convertedHits = useMemo(() => {
      return instantHits.map(hit => {
        const source = hit._source || hit;
        const { _id, objectID, ...sourceData } = source;

        return {
          _id: hit._id || hit.objectID || hit.id,
          _source: sourceData
        };
      });
    }, [instantHits]);

    return (
      <CoursesHits
        hits={convertedHits}
        customHits={customHits}
        addCourseToHits={addCourseToHits}
        currentUserRole={currentUserRole}
        currentUserEmail={currentUserEmail}
        onFocusCourse={onFocusCourse}
      />
    );
  });

  return (
    <Card className="mb-3 material-shadow">
      <CardBody>
        <InstantSearch
          searchClient={searchClient}
          indexName="courses"
          future={{ preserveSharedStateOnUnmount: true }}
          routing={false}
        >
          <Configure {...configureProps} />

          <Row className="mb-3">
            <Col md={12}>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="mb-0">Cursos</h5>
                <Button
                  color="secondary"
                  size="sm"
                  onClick={toggleFiltros}
                >
                  {filtrosOpen ? "Ocultar" : "Mostrar"} Filtros
                </Button>
              </div>

              <CustomSearchBox
                queryFields={searchFields}
                placeholder="Buscar cursos..."
              />
              <CustomStats />
            </Col>
          </Row>

          <Collapse isOpen={filtrosOpen}>
            <Row className="mb-3">
              {filterConfigs.map((filterConfig) => (
                <Col md={6} key={filterConfig.id} className="mb-3">
                  <Card>
                    <CardBody>
                      <h6>{filterConfig.label}</h6>
                      <RefinementListWithTracking
                        attribute={filterConfig.field}
                        searchable={false}
                        onRefinementChange={updateRefinements}
                      />
                    </CardBody>
                  </Card>
                </Col>
              ))}
            </Row>

            <Row className="mb-3">
              <Col md={12}>
                <CustomClearRefinements />
              </Col>
            </Row>
          </Collapse>

          <Row className="mb-3">
            <Col md={12}>
              <div className="d-flex justify-content-between align-items-center">
                <CustomSortBy
                  options={[
                    { label: "Relevancia", field: "_score", order: "desc", defaultOption: true },
                    { label: "Más recientes", field: "created_at", order: "desc" },
                    { label: "Más antiguos", field: "created_at", order: "asc" },
                    { label: "Título A-Z", field: "title.raw", order: "asc" },
                    { label: "Título Z-A", field: "title.raw", order: "desc" }
                  ]}
                  onSortChange={(option) => {
                    setSortOption({ field: option.field, order: option.order });
                  }}
                />
              </div>
            </Col>
          </Row>

          <CoursesHitsWrapper
            customHits={customHits}
            addCourseToHits={addCourseToHits}
            currentUserRole={currentUserRole}
            currentUserEmail={currentUserEmail}
            onFocusCourse={onFocusCourse}
          />

          <CustomPagination />
        </InstantSearch>
      </CardBody>
    </Card>
  );
};

export default CoursesIndex;
